/**
 * Central Phaser icon helper — loads Phosphor SVGs (or PNG overrides)
 * as textures and places tinted images. Screens never hardcode Phosphor names.
 * Gear item PNGs preload alongside the semantic registry (see gearSprites.ts).
 */

import Phaser from 'phaser';
import { UI } from '../theme';
import type { ItemDef, ItemRarity } from '../../../data/equipment';
import {
  ICON_REGISTRY,
  iconAssetPath,
  iconTextureKey,
  EQUIP_SLOT_ICONS,
  type IconId,
} from './registry';
import {
  allGearSpriteUrls,
  gearSpriteTextureKey,
  resolveGearSprite,
} from './gearSprites';

export type GameIconOptions = {
  /** Display size in design pixels (square). */
  size?: number;
  /** Tint color (number). Defaults to parchment. */
  color?: number;
  /** When true, skip tint (used for authored gear PNGs). */
  noTint?: boolean;
  alpha?: number;
  disabled?: boolean;
  originX?: number;
  originY?: number;
  depth?: number;
};

const RASTER_SIZE = 64;

/** Queue all registered icon textures on the scene loader. */
export function preloadGameIcons(scene: Phaser.Scene): void {
  for (const id of Object.keys(ICON_REGISTRY) as IconId[]) {
    const def = ICON_REGISTRY[id];
    const key = iconTextureKey(id);
    if (scene.textures.exists(key)) {
      continue;
    }
    const url = iconAssetPath(def);
    if (url.endsWith('.svg')) {
      scene.load.svg(key, url, { width: RASTER_SIZE, height: RASTER_SIZE });
    } else {
      scene.load.image(key, url);
    }
  }
  preloadGearSprites(scene);
}

/** Queue imported gear item PNGs (idempotent). */
export function preloadGearSprites(scene: Phaser.Scene): void {
  for (const url of allGearSpriteUrls()) {
    const key = gearSpriteTextureKey(url);
    if (scene.textures.exists(key)) continue;
    scene.load.image(key, url);
  }
}

export function hardenGameIconTextures(scene: Phaser.Scene): void {
  for (const id of Object.keys(ICON_REGISTRY) as IconId[]) {
    const key = iconTextureKey(id);
    if (scene.textures.exists(key)) {
      scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }
  }
  for (const url of allGearSpriteUrls()) {
    const key = gearSpriteTextureKey(url);
    if (scene.textures.exists(key)) {
      // Pixel art — nearest neighbor keeps crisp edges at UI scale.
      scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
    }
  }
}

/**
 * Place a semantic icon centered at (x, y).
 * Returns the Image so callers can tween / recolor.
 */
export function addGameIcon(
  scene: Phaser.Scene,
  id: IconId,
  x: number,
  y: number,
  options: GameIconOptions = {},
): Phaser.GameObjects.Image {
  const size = options.size ?? 22;
  const color = options.color ?? UI.parchment;
  const key = iconTextureKey(id);
  const tex = scene.textures.exists(key) ? key : iconTextureKey('ui.warning');
  const img = scene.add
    .image(x, y, tex)
    .setDisplaySize(size, size)
    .setOrigin(options.originX ?? 0.5, options.originY ?? 0.5)
    .setAlpha(options.disabled ? (options.alpha ?? 0.4) : (options.alpha ?? 1));
  if (!options.noTint) {
    img.setTint(color);
  }
  if (options.depth != null) {
    img.setDepth(options.depth);
  }
  return img;
}

export type ItemIconOptions = GameIconOptions & {
  /** Phosphor fallback tint when no gear PNG resolves. */
  fallbackColor?: number;
  /** Prefer instanceId so each owned item keeps a stable icon pick. */
  instanceId?: string;
};

/**
 * Place an item icon: authored gear PNG when available, else slot Phosphor.
 * Gear PNGs are untinted (rarity band is already encoded in the art pool).
 */
export function addItemIcon(
  scene: Phaser.Scene,
  def: ItemDef | undefined,
  rarity: ItemRarity | string,
  x: number,
  y: number,
  options: ItemIconOptions = {},
): Phaser.GameObjects.Image {
  const size = options.size ?? 22;
  const resolved = def
    ? resolveGearSprite(def, rarity, options.instanceId ?? '')
    : null;
  if (resolved && scene.textures.exists(resolved.textureKey)) {
    const img = scene.add
      .image(x, y, resolved.textureKey)
      .setDisplaySize(size, size)
      .setOrigin(options.originX ?? 0.5, options.originY ?? 0.5)
      .setAlpha(options.disabled ? (options.alpha ?? 0.4) : (options.alpha ?? 1));
    if (options.depth != null) img.setDepth(options.depth);
    return img;
  }
  const iconSlot = def?.allowedSlots[0] ?? 'helmet';
  const iconId = EQUIP_SLOT_ICONS[iconSlot] ?? 'equip.helmet';
  return addGameIcon(scene, iconId, x, y, {
    ...options,
    size,
    color: options.fallbackColor ?? options.color ?? UI.parchment,
  });
}

/**
 * Icon + optional invisible hit pad (≥44px) for touch targets.
 * Container origin is center.
 */
export function addGameIconButton(
  scene: Phaser.Scene,
  id: IconId,
  x: number,
  y: number,
  onClick: () => void,
  options: GameIconOptions & { hitSize?: number; label?: string } = {},
): Phaser.GameObjects.Container {
  const hitSize = Math.max(44, options.hitSize ?? 44);
  const icon = addGameIcon(scene, id, 0, 0, options);
  const hit = scene.add
    .rectangle(0, 0, hitSize, hitSize, 0xffffff, 0.001)
    .setInteractive({ useHandCursor: !options.disabled });
  const children: Phaser.GameObjects.GameObject[] = [icon, hit];
  if (!options.disabled) {
    hit.on('pointerup', onClick);
  }
  return scene.add.container(x, y, children);
}

export function setGameIconColor(
  img: Phaser.GameObjects.Image,
  color: number,
): void {
  img.setTint(color);
}
